import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'
import { skyProductURL, skyQueryKey } from './skyEndpoints'
import type { CampaignTreeGroup } from '../../components/social-media/campaignTreeModel'

export interface UseSkyCampaignTreeResult {
  groups: CampaignTreeGroup[]
  isLoading: boolean
  error: Error | null
}

/**
 * The product→campaign map, for the admin tree (T3.4).
 *
 * 🔴 THIS IS SKY-INTERNAL COMMERCIAL INFORMATION and the server serves it for
 * SELECTABLE products only — `all` today. Every other product gets a 404, which
 * is why this hook is called from the `all` page and nowhere else. Do not lift
 * it into a shared layout: the withholding is the point, and `SkyProductCard`
 * withholds the same map deliberately.
 */
export function useSkyCampaignTree(product: string, enabled = true): UseSkyCampaignTreeResult {
  const account = useAccount()

  const query = useQuery({
    queryKey: skyQueryKey(product, 'campaign_tree'),
    enabled: Boolean(product && account.user && enabled),
    // The manifest changes at deploy time, not during a session.
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async (): Promise<CampaignTreeGroup[]> => {
      try {
        const res = await axios.get<{ groups: CampaignTreeGroup[] }>(
          skyProductURL(product, '/campaign-tree'),
        )
        return res.data.groups ?? []
      } catch (err) {
        throw new Error(extractErrorMessage(err))
      }
    },
  })

  return {
    groups: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
  }
}

export default useSkyCampaignTree
